import type { Crm } from "../components/CrmUi/useCrm";

/**
 * The app's support chat: Everything's website chat (the same widget, key
 * and colours as boringplugins.com's), in the CRM's colour. It replaced
 * HubSpot's chat (portal 40131273) in the app on 2026-10-22.
 *
 *   loadSupportChat({ crm, appearance })  once per page/CRM (components/Layout/SupportChat)
 *   openSupportChat("I'd like 5 more licenses")  Help, checkout, Contact us, Buy licenses
 *
 * Opening uses widget.js's host bridge: an `everything-chat:host-open` window
 * event, which it forwards to the frame (and replays once the frame is ready),
 * optionally with text left waiting in the composer.
 */
const WIDGET_SRC = "https://everything.thetool.company/widget.js";
const EMBED_KEY = "wc_4ab65b56711a8ca91ef4d0e94ffa5502958267fcd9e7f400";
/** Where a click goes when the chat cannot load (blocked script, offline). */
const FALLBACK_URL = "https://everything.thetool.company/book/website";

/** The colour Everything's embeds wear per CRM: the chat (data-color) and the onboarding calendar (?color=). */
export const EMBED_COLOR: Record<Crm, string> = { hubspot: "#c93700", outreach: "#5951ff" };

type ChatWindow = Window & { __everythingChatLoaded?: boolean };

let pendingOpen: { message?: string } | null = null;

export function removeSupportChat(): void {
  document.getElementById("everything-chat-widget")?.remove();
  document.querySelectorAll("script[data-support-chat]").forEach((s) => s.remove());
  // widget.js adds a phone-layout <style> for its frame on every load.
  document.querySelectorAll("style").forEach((s) => {
    if (s.textContent?.includes("#everything-chat-widget")) s.remove();
  });
  (window as ChatWindow).__everythingChatLoaded = false;
}

/** (Re)loads the widget in this CRM's colour. `appearance` forces light or dark; omitted, it follows the page. */
export function loadSupportChat({ crm, appearance }: { crm: Crm; appearance?: "light" | "dark" }): void {
  removeSupportChat();
  const script = document.createElement("script");
  script.src = WIDGET_SRC;
  script.async = true;
  script.dataset.key = EMBED_KEY;
  script.dataset.color = EMBED_COLOR[crm];
  if (appearance) script.dataset.appearance = appearance;
  script.dataset.supportChat = "";
  script.onload = () => {
    if (!pendingOpen) return;
    const { message } = pendingOpen;
    pendingOpen = null;
    dispatchOpen(message);
  };
  document.body.appendChild(script);
}

const dispatchOpen = (message?: string) => window.dispatchEvent(new CustomEvent("everything-chat:host-open", { detail: { message } }));

/** Opens the support chat, with `message` waiting in its composer if given. */
export function openSupportChat(message?: string): void {
  if ((window as ChatWindow).__everythingChatLoaded) {
    dispatchOpen(message);
    return;
  }
  if (document.querySelector("script[data-support-chat]")) {
    // Still loading: open it the moment widget.js is in.
    pendingOpen = { message };
    return;
  }
  window.open(FALLBACK_URL, "_blank", "noopener,noreferrer");
}
